import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ArAgingClientRow } from '@bits/shared/zod';
import { formatInr, paise } from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors } from '@/lib/sort';

export const billingArAgingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/ar-aging',
  component: BillingArAgingPage,
});

type AgingSortKey = 'client' | 'current' | 'b0_30' | 'b31_60' | 'b61_90' | 'b90_plus' | 'total';
const EMPTY_ROWS: ArAgingClientRow[] = [];
const agingSortAccessors: SortAccessors<ArAgingClientRow, AgingSortKey> = {
  client: (r) => r.clientName,
  current: (r) => r.current,
  b0_30: (r) => r.b0_30,
  b31_60: (r) => r.b31_60,
  b61_90: (r) => r.b61_90,
  b90_plus: (r) => r.b90_plus,
  total: (r) => r.total,
};

function todayIso(): string {
  const d = new Date();
  const off = (5 * 60 + 30) * 60 * 1000;
  return new Date(d.getTime() + off).toISOString().slice(0, 10);
}

function BillingArAgingPage(): JSX.Element {
  const [asOf, setAsOf] = React.useState<string>(todayIso());

  const query = useQuery({
    queryKey: ['ar-aging', asOf],
    queryFn: () => api.getArAging(asOf),
  });

  const data = query.data;
  const { rows, ...sorter } = useSortedRows(data?.rows ?? EMPTY_ROWS, agingSortAccessors);

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">AR aging</h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Unpaid balances bucketed by days-overdue. Credit notes reduce the ref invoice's due amount.
          </p>
        </div>
      </div>

      <div className="flex items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label>As of</Label>
          <Input type="date" value={asOf} onChange={(e) => setAsOf(e.currentTarget.value)} />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Totals</CardTitle>
          <CardDescription>All clients</CardDescription>
        </CardHeader>
        <CardContent>
          {data ? (
            <div className="grid grid-cols-2 gap-x-3 gap-y-4 text-sm sm:grid-cols-3 lg:grid-cols-6 [&_.font-mono]:whitespace-nowrap">
              <div><div className="text-xs text-[hsl(var(--muted-foreground))]">Current</div><div className="font-mono">{formatInr(paise(data.totals.current))}</div></div>
              <div><div className="text-xs text-[hsl(var(--muted-foreground))]">0–30</div><div className="font-mono">{formatInr(paise(data.totals.b0_30))}</div></div>
              <div><div className="text-xs text-[hsl(var(--muted-foreground))]">31–60</div><div className="font-mono">{formatInr(paise(data.totals.b31_60))}</div></div>
              <div><div className="text-xs text-[hsl(var(--muted-foreground))]">61–90</div><div className="font-mono">{formatInr(paise(data.totals.b61_90))}</div></div>
              <div><div className="text-xs text-[hsl(var(--muted-foreground))]">90+</div><div className="font-mono">{formatInr(paise(data.totals.b90_plus))}</div></div>
              <div><div className="text-xs text-[hsl(var(--muted-foreground))]">Total</div><div className="font-mono font-semibold">{formatInr(paise(data.totals.total))}</div></div>
            </div>
          ) : <div className="text-sm">Loading…</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>By client</CardTitle>
          <CardDescription>{data?.rows.length ?? 0} clients with open receivables</CardDescription>
        </CardHeader>
        <CardContent>
          {data && data.rows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-[hsl(var(--muted-foreground))]">
                  <tr className="text-left">
                    <SortableTh sortKey="client" controller={sorter} className="py-2 pr-3">
                      Client
                    </SortableTh>
                    <SortableTh sortKey="current" controller={sorter} align="right" className="py-2 pr-3">
                      Current
                    </SortableTh>
                    <SortableTh sortKey="b0_30" controller={sorter} align="right" className="py-2 pr-3">
                      0–30
                    </SortableTh>
                    <SortableTh sortKey="b31_60" controller={sorter} align="right" className="py-2 pr-3">
                      31–60
                    </SortableTh>
                    <SortableTh sortKey="b61_90" controller={sorter} align="right" className="py-2 pr-3">
                      61–90
                    </SortableTh>
                    <SortableTh sortKey="b90_plus" controller={sorter} align="right" className="py-2 pr-3">
                      90+
                    </SortableTh>
                    <SortableTh sortKey="total" controller={sorter} align="right" className="py-2 pr-3">
                      Total
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.clientId} className="border-t border-[hsl(var(--border))]">
                      <td className="py-2 pr-3">{r.clientName}</td>
                      <td className="py-2 pr-3 text-right font-mono">{formatInr(paise(r.current))}</td>
                      <td className="py-2 pr-3 text-right font-mono">{formatInr(paise(r.b0_30))}</td>
                      <td className="py-2 pr-3 text-right font-mono">{formatInr(paise(r.b31_60))}</td>
                      <td className="py-2 pr-3 text-right font-mono">{formatInr(paise(r.b61_90))}</td>
                      <td className="py-2 pr-3 text-right font-mono">{formatInr(paise(r.b90_plus))}</td>
                      <td className="py-2 pr-3 text-right font-mono font-semibold">{formatInr(paise(r.total))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-sm text-[hsl(var(--muted-foreground))]">Nothing due.</div>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
